import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Car, Calendar, CheckCircle, XCircle, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function SharedVehicle() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedVehicle = async () => {
      try {
        const response = await axios.get(`${API}/vehicle/shared/${token}`);
        setVehicleData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load shared vehicle");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedVehicle();
    }
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-gray-900 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Vehicle Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1"></div>
            <div className="flex items-center justify-center flex-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png" 
                alt="IsItTaxed.co.uk" 
                className="h-16 w-auto cursor-pointer"
                style={{ objectFit: 'contain' }}
                onClick={() => navigate('/')}
              />
            </div>
            <div className="flex-1 flex justify-end">
              <Button 
                onClick={() => navigate('/')}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Shared Badge */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full mb-2">
              <Car className="h-4 w-4" />
              <span className="text-sm font-semibold">Shared Vehicle Lookup</span>
            </div>
            <p className="text-gray-600">A premium user shared this vehicle information with you</p>
          </div>

          {/* Vehicle Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              {/* Registration */}
              <div className="uk-plate-container mb-6" style={{ justifyContent: 'center' }}>
                <div className="uk-plate uk-plate-compact">
                  <input
                    type="text"
                    value={vehicleData.registration}
                    readOnly
                    className="uk-plate-input uk-plate-input-compact"
                  />
                </div>
              </div>

              {/* Vehicle Image (Only if shared by premium) */}
              {vehicleData.image_url && (
                <div className="mb-6">
                  <div className="relative rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={vehicleData.image_url}
                      alt={`${vehicleData.make} ${vehicleData.model}`}
                      className="w-full h-auto"
                      style={{ maxHeight: '400px', objectFit: 'contain' }}
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2">
                      <Crown className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-semibold">Premium Feature</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Vehicle Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Make</p>
                  <p className="font-semibold">{vehicleData.make || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Model</p>
                  <p className="font-semibold">{vehicleData.model || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Colour</p>
                  <p className="font-semibold">{vehicleData.colour || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Year</p>
                  <p className="font-semibold">{vehicleData.year_of_manufacture || 'N/A'}</p>
                </div>
              </div>

              {/* Tax & MOT Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`rounded-full p-2 ${vehicleData.tax_status === 'Taxed' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {vehicleData.tax_status === 'Taxed' ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <XCircle className="h-6 w-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Tax Status</p>
                        <p className="font-bold text-lg">{vehicleData.tax_status || 'Unknown'}</p>
                      </div>
                    </div>
                    {vehicleData.tax_due_date && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>Expires: {formatDate(vehicleData.tax_due_date)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`rounded-full p-2 ${vehicleData.mot_status === 'Valid' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {vehicleData.mot_status === 'Valid' ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <XCircle className="h-6 w-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">MOT Status</p>
                        <p className="font-bold text-lg">{vehicleData.mot_status || 'Unknown'}</p>
                      </div>
                    </div>
                    {vehicleData.mot_expiry_date && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>Expires: {formatDate(vehicleData.mot_expiry_date)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Upgrade CTA */}
          <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <Crown className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Want Unlimited Vehicle Checks Like This?</h3>
                <p className="text-gray-300 mb-6">
                  Upgrade to Premium for unlimited searches with vehicle images, full history, and more!
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold"
                  >
                    Try Free Now
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                  >
                    View Premium Plans
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="text-center mt-8 text-sm text-gray-600">
            <p>Data provided by DVLA Vehicle Enquiry Service</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default SharedVehicle;
